import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SyutSelect } from 'components/ui/SyutSelect';
import { SETTLEMENT_MODE_OPTIONS, type SettlementMode } from 'api/types/settlement.types';
import type { TaxPack } from 'api/types/tax.types';
import GstinField from './GstinField';
import GstRegisteredToggle from './GstRegisteredToggle';
import { findState, isDomesticCountry, stateByCode } from './taxAddress';

/**
 * ============================================================================
 * BILLING & TAX — shared by the Agent, Corporate and OTA modals (PMS slices 3a, 3a-ii)
 * ============================================================================
 * Tax registration
 *   Registered (Yes / No) is what the tax engine reads. Registered: the GSTIN is
 *   checked by the engine when the field is left and its state is the party's tax
 *   state (stored by the server, never typed). Not registered: no GSTIN; the tax
 *   state is the address state, picked from the tax pack's list. A GSTIN whose
 *   state differs from the address state is allowed (a branch registration) but
 *   flagged — invoices follow the GSTIN.
 * Invoicing & settlement
 *   Supply type comes from the tax pack (STANDARD, SEZ, export …) and changes what
 *   the engine charges. The default settlement is copied onto new bookings; credit
 *   options need the party to be credit eligible.
 * ============================================================================
 */

export interface PartyBillingValues {
  isGstRegistered: boolean;
  gstNumber: string;
  gstLegalName: string;
  defaultSupplyType: string;
  defaultSettlementMode: SettlementMode;
  showRatesOnGuestStatement: boolean;
  /** '' = the property default; FULL / NONE / ASK (slice 7c-2). */
  earlyDepartureRetention: string;
}

const RETENTION_OPTIONS = [
  { value: '', label: 'Property default' },
  { value: 'FULL', label: 'Charge the full stay (retain unused nights)' },
  { value: 'NONE', label: 'Charge the nights stayed only' },
  { value: 'ASK', label: 'Ask at check-out' },
];

interface Props {
  kind: 'AGENT' | 'CORPORATE' | 'OTA';
  mode: 'create' | 'edit' | 'copy';
  values: PartyBillingValues;
  isCreditEligible: boolean;
  onChange: (patch: Partial<PartyBillingValues>) => void;
  /** The tax pack (states, supply types); null while loading or when not configured. */
  pack: TaxPack | null;
  /** Address state name and country, edited in the address section. */
  addressState: string;
  addressCountry?: string;
  /** Where the address is edited, for the hints ("Address & Contact"). */
  addressSectionTitle: string;
  gstError?: string;
  /** Extra identity fields (PAN) rendered beside the legal name. */
  extra?: React.ReactNode;
  disabled?: boolean;
}

/** What a newly credit-eligible party bills by default. */
const creditDefault = (kind: Props['kind']): SettlementMode => (kind === 'CORPORATE' ? 'CREDIT_FULL' : 'CREDIT_BOOKED');

const subHeading: React.CSSProperties = {
  fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 0.5rem',
  textTransform: 'uppercase', letterSpacing: '0.03em',
};

const PartyBillingSection: React.FC<Props> = ({
  kind, mode, values, isCreditEligible, onChange, pack, addressState, addressCountry, addressSectionTitle,
  gstError, extra, disabled,
}) => {
  // Credit needs eligibility: losing it moves a credit default back to Guest pays;
  // gaining it on a new party suggests the usual credit default.
  const prevEligible = useRef(isCreditEligible);
  useEffect(() => {
    if (prevEligible.current === isCreditEligible) return;
    prevEligible.current = isCreditEligible;
    const current = SETTLEMENT_MODE_OPTIONS.find((o) => o.value === values.defaultSettlementMode);
    if (!isCreditEligible && current?.credit) onChange({ defaultSettlementMode: 'GUEST' });
    else if (isCreditEligible && mode !== 'edit' && values.defaultSettlementMode === 'GUEST') onChange({ defaultSettlementMode: creditDefault(kind) });
  }, [isCreditEligible, values.defaultSettlementMode, mode, kind, onChange]);

  // The state the GSTIN encodes, as last checked by the engine.
  const [gstinState, setGstinState] = useState<string | null>(null);
  const onGstinChecked = useCallback((code: string | null) => setGstinState(code), []);

  const domestic = isDomesticCountry(addressCountry, pack?.countryCode);
  const address = findState(pack, addressState);
  const fromGstin = stateByCode(pack, gstinState);

  const settlementOptions = SETTLEMENT_MODE_OPTIONS.map((o) => ({
    value: o.value,
    label: o.credit && !isCreditEligible ? `${o.label} (needs credit eligible)` : o.label,
    isDisabled: o.credit && !isCreditEligible,
  }));
  const settlementHint = SETTLEMENT_MODE_OPTIONS.find((o) => o.value === values.defaultSettlementMode)?.hint;

  const supplyOptions = useMemo(() => {
    const list = (pack?.supplyTypes ?? []).map((s) => ({
      value: s.supplyCode,
      label: s.supplyName ? `${s.supplyName} (${s.supplyCode})` : s.supplyCode,
    }));
    if (values.defaultSupplyType && !list.some((o) => o.value === values.defaultSupplyType))
      list.unshift({ value: values.defaultSupplyType, label: values.defaultSupplyType });
    return list;
  }, [pack, values.defaultSupplyType]);
  const supply = pack?.supplyTypes.find((s) => s.supplyCode === values.defaultSupplyType);

  // What the server will store as the tax state, said in words.
  let taxState: { tone: 'ok' | 'muted' | 'warn'; text: string };
  if (values.isGstRegistered) {
    taxState = fromGstin
      ? { tone: 'ok', text: `${fromGstin.placeName} (${fromGstin.placeCode}) — from the GSTIN` }
      : { tone: 'muted', text: 'Taken from the GSTIN once it is entered.' };
  } else if (!domestic) {
    taxState = { tone: 'muted', text: 'None — the address is outside the country. Choose an export supply type if applicable.' };
  } else if (address) {
    taxState = { tone: 'ok', text: `${address.placeName} (${address.placeCode}) — from the address state` };
  } else {
    taxState = { tone: 'warn', text: `Pick the state on ${addressSectionTitle}; an unregistered party is taxed by its address state.` };
  }

  const mismatch = values.isGstRegistered && fromGstin && address && fromGstin.placeCode !== address.placeCode;

  return (
    <div>
      <div style={subHeading}>Tax registration</div>
      <div className="form-grid-2">
        <GstRegisteredToggle value={values.isGstRegistered} onChange={(v) => onChange({ isGstRegistered: v })} disabled={disabled} />
        <div className="form-group">
          <label className="form-label">Tax state</label>
          <div style={{
            minHeight: '2.25rem', display: 'flex', alignItems: 'center', fontSize: '0.85rem',
            color: taxState.tone === 'ok' ? 'var(--text-primary)' : taxState.tone === 'warn' ? 'var(--warning, #b45309)' : 'var(--text-muted)',
          }}>
            {taxState.text}
          </div>
        </div>
        {values.isGstRegistered && (
          <GstinField
            label="GSTIN *"
            value={values.gstNumber}
            onChange={(gstNumber) => onChange({ gstNumber })}
            onChecked={onGstinChecked}
            error={gstError}
            disabled={disabled}
            emptyHint="Required for a registered party. The tax state is read from it."
          />
        )}
        <div className="form-group" data-field="gstLegalName">
          <label className="form-label">Legal name {values.isGstRegistered ? '(as registered)' : ''}</label>
          <input
            type="text"
            className="form-input"
            value={values.gstLegalName}
            maxLength={200}
            placeholder="Printed on tax invoices; empty = the name on General"
            onChange={(e) => onChange({ gstLegalName: e.target.value })}
            disabled={disabled}
          />
        </div>
        {extra}
      </div>
      {mismatch && (
        <div style={{
          margin: '0.25rem 0 0.5rem', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem',
          background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)',
        }}>
          The GSTIN is registered in {fromGstin!.placeName}, but the address state is {address!.placeName}.
          Invoices use the GSTIN state. Check the GSTIN if this party has no registration in {fromGstin!.placeName}.
        </div>
      )}

      <div style={{ ...subHeading, marginTop: '1rem' }}>Invoicing &amp; settlement</div>
      <div className="form-grid-2">
        <div className="form-group" data-field="defaultSupplyType">
          <SyutSelect
            label="Supply type"
            options={supplyOptions}
            value={values.defaultSupplyType}
            onChange={(v) => onChange({ defaultSupplyType: (v as string) || 'STANDARD' })}
            isClearable={false}
            isDisabled={disabled || supplyOptions.length === 0}
            size="md"
            menuPortalTarget={document.body}
            helperText={supply && supply.taxTreatment !== 'TAXABLE'
              ? `${supply.taxTreatment === 'ZERO_RATED' ? 'Zero-rated' : 'Exempt'}: invoices to this party carry no GST.`
              : 'How invoices to this party are taxed. Standard for a normal domestic party.'}
          />
        </div>
        <div className="form-group" data-field="defaultSettlementMode">
          <SyutSelect
            label="Default settlement"
            options={settlementOptions}
            value={values.defaultSettlementMode}
            onChange={(v) => onChange({ defaultSettlementMode: ((v as string) || 'GUEST') as SettlementMode })}
            isClearable={false}
            isDisabled={disabled}
            size="md"
            menuPortalTarget={document.body}
            helperText={settlementHint ? `${settlementHint} Copied onto new bookings; Front Office can change it per booking.` : undefined}
          />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: disabled ? 'default' : 'pointer', minHeight: '2.25rem' }}>
            <input
              type="checkbox"
              checked={values.showRatesOnGuestStatement}
              onChange={(e) => onChange({ showRatesOnGuestStatement: e.target.checked })}
              disabled={disabled}
              style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              Show rates on the guest statement{' '}
              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>(off: the guest sees "settled by" without amounts)</span>
            </span>
          </label>
        </div>
        <div className="form-group">
          <SyutSelect
            label="Early departure"
            options={RETENTION_OPTIONS}
            value={values.earlyDepartureRetention}
            onChange={(v) => onChange({ earlyDepartureRetention: (v as string) || '' })}
            isClearable={false}
            isDisabled={disabled}
            size="md"
            menuPortalTarget={document.body}
            helperText="What this party's bookings are charged for unused nights when the guest leaves early. Waiving it at check-out needs the waive-retention permission."
          />
        </div>
      </div>
    </div>
  );
};

export default PartyBillingSection;
